import {
  FlexColumn,
  FlexContainer,
  CenterChildren,
} from '@editor/components/utilities/layouts';
import { hideScrollBar } from '@repo/ui/components/utilities';
import { QUERIES } from '@repo/ui/queries';
import { getIsNewThemeActive, themeStyle } from '@repo/ui/theming';
import styled, { css } from 'styled-components';

import { MinimalTestimonialWrapper } from '../Testimonial/styles';
import { HideMobile } from '../utilities/queries';

const isNewThemeActive = getIsNewThemeActive();

export const Wrapper = styled(FlexContainer)`
  justify-content: center;
  width: 100%;
  flex-wrap: wrap;
  height: 100%;
`;

export const ContentWrapper = styled(FlexColumn)`
  padding: 32px;
  box-sizing: border-box;
  width: 100%;
  flex-shrink: 0;
  overflow-y: auto;

  ${hideScrollBar};

  ${QUERIES.largeTabletAndUp} {
    width: 336px;
    padding: 32px 32px 24px;
  }
`;

export const MobileContentWrapper = styled(ContentWrapper)`
  padding: 0;
  height: 100%;
  max-height: 380px;
  width: 100%;
  flex-shrink: unset;
  flex-grow: 1;
  margin-bottom: 36px;
`;

const imageContainerStyles = css<{
  background: string;
}>`
  flex-grow: 1;

  background: ${({ background }) => background};
  background-size: cover;
  background-position-x: center;
  background-position-y: 25%; /* this is an experimental value that looks good with current content */
  min-width: 350px;
  min-height: 500px;
`;

export const ImageHideContainer = styled(HideMobile)<{
  background: string;
}>`
  ${imageContainerStyles};
`;

export const ImageContainer = styled.div<{
  background: string;
}>`
  ${imageContainerStyles};
`;

export const ImageMobileContainer = styled.div<{ background: string }>`
  ${imageContainerStyles};
  min-width: unset;
  min-height: unset;
  max-height: none;
  width: 100%;
  border-radius: ${themeStyle.radiusMedium};
`;

export const ImageComponentWrapper = styled(CenterChildren)`
  flex-grow: 1;

  height: 100%;
  max-width: 100%;

  padding: 48px;
  box-sizing: border-box;
`;

export const MobileImageComponentWrapper = styled(ImageComponentWrapper)`
  height: auto;
  padding: 12px 48px;
`;

export const TestimonialWrapper = styled(CenterChildren)`
  width: 100%;
  height: 100%;

  ${MinimalTestimonialWrapper}:first-of-type {
    align-self: flex-end;
  }

  ${MinimalTestimonialWrapper}:last-of-type {
    margin-left: 62px;
  }
`;

export const BrandsWrapper = styled(FlexColumn)`
  height: 100%;
  padding: 0 12px;
`;

export const BrandsImage = styled.img`
  width: 100%;
  height: auto;
`;

export const DesignPreview = styled.img`
  max-width: 100%;
  border-radius: ${themeStyle.radiusMedium};
  box-shadow: ${themeStyle.varShadowSoft};

  ${QUERIES.tabletAndDown} {
    max-height: 250px;
  }
`;

/**
 * Config components
 */

export const FreeTrialIconsContainer = styled(FlexColumn)`
  position: relative;
  gap: 51px;
`;

export const FreeTrialIconWrapper = styled(FlexContainer)`
  position: relative;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${() =>
    isNewThemeActive
      ? themeStyle.varTransparentMain10
      : themeStyle.varBrandSuperLight};

  &:not(:last-of-type) {
    &:after {
      content: '';
      position: absolute;
      top: 100%;
      left: calc(50% - 1px);
      height: 51px;
      border-right: 2px dotted ${themeStyle.varInkMain};
    }
  }
`;

export const FreeTrialImageComponent = styled(FlexColumn)`
  max-width: 320px;
`;

export const NewFreeTrialIconWrapper = styled(CenterChildren)`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${themeStyle.varHighlight};
`;

export const UpgradeNowContainer = styled(FlexColumn)`
  margin-top: auto;

  ${QUERIES.tabletAndDown} {
    margin-top: 10px;
  }
`;

export const TrialStepsContainer = styled(FlexColumn)`
  > div {
    // Apply a different background color to other steps except the first

    &:not(:first-child) {
      ${NewFreeTrialIconWrapper} {
        background-color: ${themeStyle.varInkLight};
      }
    }

    // Link all the steps with a line except the last one

    &:not(:last-child) {
      ${NewFreeTrialIconWrapper} {
        &:after {
          display: block;
          content: '';
          position: absolute;
          top: 48px;
          width: 5px;
          height: 48px;
          background-color: inherit;
        }
      }
    }

    //  Apply a gradient background color to the first line only

    &:first-child {
      ${NewFreeTrialIconWrapper}::after {
        background: linear-gradient(to bottom, #4148e2 38%, #e2e6ed 94.5%);
      }
    }
  }
`;
